import type { PosthogIdentity } from "../analytics/posthog-cookie";

export const REQUEST_ID_HEADER = "x-repackify-request-id";

export const REQUEST_TIMING_MESSAGE = "request-timing";

export const PUBLIC_CONVEX_READ_BUDGET_MS = 10_000;

const REQUEST_ID_SERVER_TIMING = "request-id";

const REQUEST_ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;
const SLOW_BACKEND_TOTAL_MS = 750;
const BUSY_BACKEND_CALL_COUNT = 12;
const REPORTED_CALL_LIMIT = 10;

export interface ConvexCallRecord {
  name: string;
  durationMs: number;
  attempts: number;
  ok: boolean;
}

export interface RequestScope {
  requestId: string;
  calls: ConvexCallRecord[];
  posthog: PosthogIdentity;
  firstRender: boolean;
  claimFirstRender: () => boolean;
  convexReadDeadline: number | null;
  publicConvexUnavailable: boolean;
}

declare global {
  var __repackifyRequestScope: RequestScope | undefined;
}

export function currentRequestScope(): RequestScope | undefined {
  return globalThis.__repackifyRequestScope;
}

export function currentRequestId(): string | undefined {
  return currentRequestScope()?.requestId;
}

export function currentPosthogIdentity(): PosthogIdentity {
  return currentRequestScope()?.posthog ?? {};
}

export function markRender(): void {
  const scope = currentRequestScope();
  if (scope && !scope.firstRender) scope.firstRender = scope.claimFirstRender();
}

export function convexReadTimeLeftMs(budgetMs: number): number | undefined {
  const scope = currentRequestScope();
  if (!scope) return undefined;
  scope.convexReadDeadline ??= Date.now() + budgetMs;
  return scope.convexReadDeadline - Date.now();
}

export function markPublicConvexUnavailable(): void {
  const scope = currentRequestScope();
  if (scope) scope.publicConvexUnavailable = true;
}

export function recordConvexCall(record: ConvexCallRecord): void {
  currentRequestScope()?.calls.push(record);
}

export interface ConvexTiming {
  convexCalls: number;
  convexMs: number;
  convexFailed: number;
  convexRetried: number;
  slowestConvexCalls?: ConvexCallRecord[];
}

export function requestIdServerTiming(requestId: string): string {
  return `${REQUEST_ID_SERVER_TIMING};desc=${requestId}`;
}

export function requestIdFromServerTiming(
  entries: readonly { name: string; description: string }[],
): string | undefined {
  const entry = entries.find((candidate) => candidate.name === REQUEST_ID_SERVER_TIMING);
  return entry && REQUEST_ID_PATTERN.test(entry.description)
    ? entry.description
    : undefined;
}

function readRequestId(headers: Headers, name: string): string | null {
  const value = headers.get(name)?.trim();
  return value && REQUEST_ID_PATTERN.test(value) ? value : null;
}

export function resolveEdgeRequestId(request: Request): string {
  return (
    readRequestId(request.headers, "cf-ray") ??
    readRequestId(request.headers, REQUEST_ID_HEADER) ??
    crypto.randomUUID()
  );
}

export function resolveRequestId(request: Request): string {
  return (
    readRequestId(request.headers, REQUEST_ID_HEADER) ??
    readRequestId(request.headers, "cf-ray") ??
    crypto.randomUUID()
  );
}

function isWorthBreakingDown(calls: readonly ConvexCallRecord[]): boolean {
  if (calls.length >= BUSY_BACKEND_CALL_COUNT) return true;
  let totalMs = 0;
  for (const call of calls) {
    if (!call.ok || call.attempts > 1) return true;
    totalMs += call.durationMs;
  }
  return totalMs >= SLOW_BACKEND_TOTAL_MS;
}

export function summarizeConvexCalls(
  calls: readonly ConvexCallRecord[],
): ConvexTiming {
  const timing: ConvexTiming = {
    convexCalls: calls.length,
    convexMs: calls.reduce((sum, call) => sum + call.durationMs, 0),
    convexFailed: calls.filter((call) => !call.ok).length,
    convexRetried: calls.filter((call) => call.attempts > 1).length,
  };
  if (!isWorthBreakingDown(calls)) return timing;
  return {
    ...timing,
    slowestConvexCalls: [...calls]
      .sort((left, right) => right.durationMs - left.durationMs)
      .slice(0, REPORTED_CALL_LIMIT),
  };
}
